import {Button, Dropdown, Menu} from 'antd';
import {PlusOutlined} from '@ant-design/icons';
import React from 'react';

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

import {ActionsFilter} from './actionsFilter';
import {ArchiveStateFilter} from './archiveStateFilter';
import {CampaignsFilter} from './campaignsFilter';
import {CampaignTypesFilter} from './campaignTypesFilter';
import {DateFilter} from './dateFilter';
import {UsersFilter} from './usersFilter';
import {IconWrapper} from '../../../IconWrapper';

import './campaignFilters.scss';

const FILTER_DEFAULT_STATES = {
  date: 'lifetime',
  users: [],
  campaigns: [],
  campaign_types: [],
  archive_mode: 'unarchived_only',
  actions: [],
};

const sortFilters = filters => {
  const defaultOrder = Object.keys(FILTER_DEFAULT_STATES);
  filters.sort((a, b) => defaultOrder.indexOf(a) - defaultOrder.indexOf(b));
  return filters;
};

export const CampaignFilters = ({
  campaigns,
  users,
  filterCampaigns,
  filters,
  updateGraph,
  client,
  statsMode,
  getCampaigns,
  setArchiveMode,
  archiveMode,
}: any) => {
  const [campaignsToRequest, setCampaignsToRequest] = React.useState([]);
  const [shownFilters, setShownFilters] = React.useState([]);

  React.useEffect(() => {
    const notEmptyFilters = Object.keys(FILTER_DEFAULT_STATES).filter(filter => filters[filter]?.length > 0);
    setShownFilters(notEmptyFilters);
  }, [filters]);

  const filteredCampaigns = !filters?.users?.length
    ? campaigns
    : campaigns.filter(c => filters.users.some(id => String(id) === String(c?.selectedUser?.id)));
  const handleUsersChange = users => {
    filterCampaigns({
      ...filters,
      users,
    });
    updateGraph(campaignsToRequest);
  };

  const getFiltersDropdown = (currentFilters: string[]) => {
    return (
      <Menu onClick={params => setShownFilters(sortFilters([...shownFilters, params.key]))}>
        {!currentFilters.includes('date') && (
          <Menu.Item key="date">
            <IconWrapper>
              <FontAwesomeIcon icon={['fas', 'clock']} />
            </IconWrapper>{' '}
            Time period
          </Menu.Item>
        )}
        {!currentFilters.includes('users') && (
          <Menu.Item key="users">
            <IconWrapper>
              <FontAwesomeIcon icon={['fas', 'users']} />
            </IconWrapper>{' '}
            User
          </Menu.Item>
        )}
        {!currentFilters.includes('campaigns') && (
          <Menu.Item key="campaigns">
            <IconWrapper>
              <FontAwesomeIcon icon={['fas', 'list-ul']} />
            </IconWrapper>{' '}
            Campaign
          </Menu.Item>
        )}
        {!currentFilters.includes('campaign_types') && (
          <Menu.Item key="campaign_types">
            <IconWrapper>
              <FontAwesomeIcon icon={['fas', 'object-ungroup']} />
            </IconWrapper>{' '}
            Campaign type
          </Menu.Item>
        )}
        {!currentFilters.includes('archive_mode') && (
          <Menu.Item key="archive_mode">
            <IconWrapper>
              <FontAwesomeIcon icon={['fas', 'archive']} />
            </IconWrapper>{' '}
            Archive state
          </Menu.Item>
        )}
        {!currentFilters.includes('actions') && (
          <Menu.Item key="actions">
            <IconWrapper>
              <FontAwesomeIcon icon={['fas', 'bolt']} />
            </IconWrapper>{' '}
            Action
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const handleCampaignsChangeAction = actions => {
    filterCampaigns({
      ...filters,
      actions,
    });
    updateGraph(campaignsToRequest);
  };

  const handleCampaignsChange = campaignsIds => {
    const ids = campaignsIds
      .map(campaign => {
        const filteredIds = campaigns
          .filter(item => {
            return item.name.toLowerCase().includes(campaign?.toLowerCase());
          })
          .map(item => item.id.toString());

        if (!isNaN(campaign) && !filteredIds.length) return campaign;

        return filteredIds.length > 0 ? filteredIds : [0];
      })
      .flat(1);

    filterCampaigns({
      ...filters,
      campaigns: ids,
    });
    updateGraph(ids);
    setCampaignsToRequest(ids);
  };
  const handleCampaignsChangeType = campaign_types => {
    filterCampaigns({
      ...filters,
      campaign_types,
    });
    updateGraph(campaignsToRequest);
  };

  const handleCampaignStatusChange = value => {
    getCampaigns(client.id, value);
    statsMode && updateGraph(campaignsToRequest);
    setArchiveMode(value);
  };

  const handleDateChange = date => {
    filterCampaigns({
      ...filters,
      date,
      date_range_start: '',
      date_range_end: '',
    });
    updateGraph(campaignsToRequest);
  };

  const clearAndCloseUserFilter = () => {
    handleUsersChange([]);
    setShownFilters(shownFilters.filter(filter => filter !== 'users'));
  };

  const clearAndCloseCampaignFilter = () => {
    handleCampaignsChange([]);
    setShownFilters(shownFilters.filter(filter => filter !== 'campaigns'));
  };

  const clearAndCloseCampaignTypesFilter = () => {
    handleCampaignsChangeType([]);
    setShownFilters(shownFilters.filter(filter => filter !== 'campaign_types'));
  };

  const clearAndCloseActionsFilter = () => {
    handleCampaignsChangeAction([]);
    setShownFilters(shownFilters.filter(filter => filter !== 'actions'));
  };

  const getFilter = filterType => {
    switch (filterType) {
      case 'users':
        return (
          <UsersFilter
            key={filterType}
            handleChange={handleUsersChange}
            users={users}
            filters={filters}
            clearAndClose={clearAndCloseUserFilter}
          />
        );
      case 'campaigns':
        return (
          <CampaignsFilter
            key={filterType}
            handleChange={handleCampaignsChange}
            filteredCampaigns={filteredCampaigns}
            filters={filters}
            clearAndClose={clearAndCloseCampaignFilter}
          />
        );
      case 'date':
        return (
          <DateFilter
            key={filterType}
            handleChange={handleDateChange}
            filters={filters}
            updateGraph={updateGraph}
            campaignsToRequest={campaignsToRequest}
            filterCampaigns={filterCampaigns}
          />
        );
      case 'campaign_types':
        return (
          <CampaignTypesFilter
            key={filterType}
            handleChange={handleCampaignsChangeType}
            filters={filters}
            clearAndClose={clearAndCloseCampaignTypesFilter}
          />
        );
      case 'actions':
        return (
          <ActionsFilter
            key={filterType}
            filters={filters}
            handleChange={handleCampaignsChangeAction}
            clearAndClose={clearAndCloseActionsFilter}
          />
        );
      case 'archive_mode':
        return (
          <ArchiveStateFilter
            key={filterType}
            handleChange={handleCampaignStatusChange}
            archiveMode={archiveMode}
          />
        );
    }
  };

  return (
    <div className="campaigns-page__filters">
      {shownFilters.map(getFilter)}

      {shownFilters.length < 6 && (
        <Dropdown trigger={['click']} overlay={getFiltersDropdown(shownFilters)} placement="bottom">
          <Button type="link" icon={<PlusOutlined />}>
            Add filter
          </Button>
        </Dropdown>
      )}
    </div>
  );
};
